import { useFlatfile, Sheet, Space, Workbook, useEvent, usePlugin } from '@flatfile/react'
import { Button, notification } from 'antd'
import api from '@flatfile/api'
import { useCallback, useMemo, useRef, useState } from 'react'
import { isNotEmpty } from '../../utils'
import { autocast } from '@flatfile/plugin-autocast'
import {
	applyColumnMoves,
	buildColumnMoves,
	correctExecutionPlan,
	fetchAllRecords,
	updateRecordsInChunks
} from './mapping-corrections'

const FlatFilePortal = ({ sheet, onSubmit }) => {
	const { openPortal, closePortal } = useFlatfile()
	const [fileName, setFileName] = useState(`${sheet.slug}.csv`)
	const [validData, setValidData] = useState([])
	const [isInitializing, setIsInitializing] = useState(false)
	usePlugin(autocast(sheet.slug))

	// Latest destination fields, readable from event handlers without re-subscribing
	const fieldsRef = useRef(sheet.fields)
	fieldsRef.current = sheet.fields
	// Per mapping job: the records that existed before it ran, so that a
	// correction only touches the rows that job created
	const mapJobsRef = useRef(new Map())
	// Pending post-mapping correction, awaited before data is submitted
	const pendingCorrectionRef = useRef(Promise.resolve())

	useEvent(
		'space:created',
		{},
		() => {
			setIsInitializing(false)
		}
	)

	useEvent(
		'file:created',
		{},
		async (event) => {
			try {
				const { fileId } = event.context
				const file = await api.files.get(fileId)
				setFileName(file?.data?.name)
			} catch (error) {
				console.error(error)
				notification.error({
					message: 'Error',
					description: `${error.message}`,
					placement: 'bottomRight'
				})
			}
		}
	)

	// Flatfile generates the mapping plan asynchronously and announces it by
	// moving the map job into "planning" (the same hook Flatfile's own automap
	// plugin uses). Correct the suggested plan there so exact header matches
	// win over fuzzy/learned suggestions, and remember which records already
	// exist so the post-mapping correction below only touches new rows.
	useEvent(
		'job:updated',
		{ job: 'workbook:map' },
		async (event) => {
			const { jobId } = event.context
			if (mapJobsRef.current.has(jobId)) {
				return
			}
			const state = { existingRecordIds: null, corrected: false }
			mapJobsRef.current.set(jobId, state)
			try {
				const { data: job } = await api.jobs.get(jobId)
				if (job.status !== 'planning') {
					mapJobsRef.current.delete(jobId)
					return
				}

				const { data: jobPlan } = await api.jobs.getExecutionPlan(jobId)
				const correction = correctExecutionPlan(jobPlan?.plan || {}, fieldsRef.current)
				if (correction.changed) {
					await api.jobs.updateExecutionPlan(jobId, {
						fieldMapping: correction.fieldMapping,
						unmappedSourceFields: correction.unmappedSourceFields,
						unmappedDestinationFields: correction.unmappedDestinationFields,
						fileId: job.fileId,
						jobId
					})
				}

				const sheetId = job.config?.destinationSheetId
				if (sheetId) {
					const records = await fetchAllRecords(api, sheetId)
					state.existingRecordIds = new Set(records.map((record) => record.id))
				}
			} catch (error) {
				console.error('Failed to correct mapping plan', error)
			}
		}
	)

	// The mapping screen may still execute Flatfile's original suggestion (it
	// renders the plan it fetched first and submits that on "Continue"). Once
	// the map job has run, read the plan that was actually executed and move
	// any values that landed in a column whose exact match is a different
	// field, so the review grid and the submitted data use the right product.
	useEvent(
		'job:completed',
		{ job: 'workbook:map' },
		(event) => {
			const { jobId } = event.context
			// Moving columns is not idempotent, so never run it twice for one job
			const state = mapJobsRef.current.get(jobId) || { existingRecordIds: null, corrected: false }
			if (state.corrected) {
				return
			}
			state.corrected = true
			mapJobsRef.current.set(jobId, state)
			const correction = (async () => {
				const { data: jobPlan } = await api.jobs.getExecutionPlan(jobId)
				const moves = buildColumnMoves(jobPlan?.plan?.fieldMapping, fieldsRef.current)
				const sheetId = jobPlan?.job?.config?.destinationSheetId
				if (moves.length === 0 || !sheetId) {
					return
				}
				const records = await fetchAllRecords(api, sheetId)
				const updates = records
					.filter((record) => !state.existingRecordIds?.has(record.id))
					.map((record) => ({ id: record.id, values: applyColumnMoves(record.values, moves) }))
				await updateRecordsInChunks(api, sheetId, updates)
			})().catch((error) => {
				console.error('Failed to correct mapped columns', error)
				notification.warning({
					message: 'Please review the column mapping',
					description: 'Some columns may have been matched to a similarly named field. Check the mapped data before uploading.',
					placement: 'bottomRight'
				})
			})
			pendingCorrectionRef.current = correction
		}
	)

	useEvent(
		'job:outcome-acknowledged',
		{
			operation: `sheetSubmitAction-${sheet.slug}`,
			status: 'complete'
		},
		async () => {
			if (validData.length > 0) {
				setValidData([])
				closePortal()
			}
		}
	)

	const getFormattedData = (records) => {
		const rows = []
		for (const record of records) {
			const values = record.values
			let row = {}
			for (const [key, value] of Object.entries(values)) {
				if (value.valid) {
					row[key] = isNotEmpty(value.value) ? value.value : undefined
				}
			}
			rows.push(row)
		}
		return rows
	}

	const handleOpenPortal = () => {
		setIsInitializing(true)
		try {
			openPortal()
		} catch (error) {
			setIsInitializing(false)
			notification.error({
				message: 'Error Opening CSV Importer',
				description: error.message || 'Failed to open the CSV importer. Please try again.',
				placement: 'bottomRight'
			})
		}
	}

	const spaceConfig = useMemo(() => ({
		namespace: 'nuport-web',
		metadata: {
			sidebarConfig: {
				showSidebar: false
			},
			theme: {
				root: {
					primaryColor: '#278EA5',
					buttonBorderRadius: '0px',
					badgeBorderRadius: '0px',
					checkboxBorderRadius: '0px',
					interactiveBorderRadius: '0px',
					modalBorderRadius: '0px',
					pillBorderRadius: '0px',
					popoverBorderRadius: '0px'
				}
			}
		},
		closeSpace: {
			operation: 'submitActionFg',
			onClose: () => {
				setIsInitializing(false)
			}
		}
	}), []) // Empty array since config is static

	// Memoize the Workbook config
	const workbookConfig = useMemo(() => ({
		namespace: 'nuport-web',
		name: 'Upload CSV',
		labels: ['pinned'],
		settings: {
			trackChanges: true
		}
	}), [])

	// Memoize the onSubmit callback
	const handleSubmit = useCallback(async ({ sheet }) => {
		try {
			await pendingCorrectionRef.current
			const { records } = await sheet.validData()
			const validData = getFormattedData(records)
			setValidData(validData)
			onSubmit?.({ validData, fileName })
		} catch (error) {
			console.error(error)
			notification.error({
				message: 'Error',
				description: `${error.message}`,
				placement: 'bottomRight'
			})
		}
	}, [fileName, onSubmit]) // Add dependencies used in the callback

	// Memoize the onRecordHook callback
	const handleRecordHook = useCallback((record) => {
		return record
	}, [])

	return (
		<div>
			<Button
				disabled={isInitializing}
				size='large'
				type='primary'
				shape='round'
				onClick={handleOpenPortal}
			>
				{isInitializing ? 'Loading...' : 'Get Started'}
			</Button>
			<Space config={spaceConfig}>
				<Workbook config={workbookConfig}>
					<Sheet
						config={sheet}
						onSubmit={handleSubmit}
						onRecordHook={handleRecordHook}
					/>
				</Workbook>
			</Space>
		</div>
	)
}

export default FlatFilePortal
